import { defineComponent as _defineComponent } from 'vue'

interface OzTabsButtonProps {
  /**
   * A string to identify the tab.
   */
  // eslint-disable-next-line vue/no-reserved-props
  key: string
  /**
   * The text to display on the tab.
   * If `iconName` is provided, this will be used as the `aria-label` instead.
   */
  text: string
  /**
   * The name of the icon to display on the tab.
   * We don't display `text` if this is provided.
   */
  iconName?: string
  /**
   * The name of the icon to display on the tab when it's active.
   */
  activeIconName?: string
  /**
   * The size of the icon to display on the tab.
   */
  iconSize?: 16 | 24
  /**
   * `aria-controls` attribute.
   */
  ariaControls?: string
  /**
   * `href` attribute.
   * This also makes the tab a link.
   */
  href?: string
  /**
   * should the tab be disabled or not
   */
  disabled?: boolean
  /**
   * Id for querying tab in tests
   */
  testId?: string
}

/**
 * Accommodates the elements and attributes according to the use.
 * If 'Tabs', the component will be structured according to the ARIA tab list specification.
 * If 'Navigation', the component will be structured according to the ARIA navigation specification.
 */
enum OzTabsAccessibilityRole {
  Tabs = 'Tabs',
  Navigation = 'Navigation',
}

/**
 * Tabs placement mode.
 * Hug: Gap between tabs is dynamic, and each tab occupies as little space as possible.
 * Stretch: No gap between tabs, and each tab has the same width.
 */
enum OzTabsPlacementMode {
  Hug = 'Hug',
  Stretch = 'Stretch',
}

enum OzTabsSizePreset {
  ExtraSmall = 'ExtraSmall',
  Small = 'Small',
}
export { OzTabsAccessibilityRole, OzTabsPlacementMode, OzTabsSizePreset }
export type { OzTabsButtonProps }
const __default__ = {}

import { observeSizeChange } from '@@/bits/resize_observer'
import { defineAsyncComponent } from '@@/bits/vue'
import OzPlainButton, { OzPlainButtonColorScheme } from '@@/library/v4/components/OzPlainButton.vue'
import tinykeys from 'tinykeys'
import type { ComponentPublicInstance } from 'vue'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch, withDefaults } from 'vue'

interface Tab {
  key: string
  el: HTMLElement
}


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'OzTabs',
  props: {
    tabs: null,
    ariaLabel: null,
    accessibilityRole: { default: OzTabsAccessibilityRole.Tabs },
    placementMode: { default: OzTabsPlacementMode.Hug },
    currentTabKey: { default: undefined },
    sizePreset: { default: OzTabsSizePreset.Small },
    darkMode: { type: [Boolean, String], default: 'auto' },
    tabsContainerClasses: { default: undefined },
    indicatorWidth: { default: 0 },
    indicatorHeight: { default: 0 }
  },
  emits: ["activate-tab"],
  setup(__props: any, { emit }: { emit: ({
  (
    e: 'activate-tab',
    event: Event,
    tab: {
      key: string
    },
  ): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { tabs: OzTabsButtonProps[], ariaLabel: string, accessibilityRole: OzTabsAccessibilityRole, placementMode: OzTabsPlacementMode, currentTabKey: string, sizePreset: OzTabsSizePreset, darkMode: boolean | 'auto', tabsContainerClasses: string, indicatorWidth: number, indicatorHeight: number };

const OzIcon = defineAsyncComponent(() => import('@@/library/v4/components/OzIcon.vue'))



const currentTabId = computed(() => props.currentTabKey || props.tabs[0].key)



const resizeObserver = ref<ResizeObserver | null>(null)
const rootEl = ref<HTMLDivElement | null>(null)
const indicator = ref<HTMLDivElement | null>(null)
const list = ref<HTMLUListElement | null>(null)
const tabsRef = ref<Array<Tab | null>>(Array(props.tabs.length).fill(null))

const setTabRef = (el: Element | ComponentPublicInstance | null, index: number, tab: OzTabsButtonProps) => {
  if (el === null) return
  tabsRef.value[index] = { key: tab.key, el: el as HTMLElement }
}

const highlightedElement = computed<HTMLDivElement | null>(() => {
  const tabEl = tabsRef.value.find((tab) => tab?.key === currentTabId.value)?.el as HTMLDivElement
  return tabEl ?? null
})

const wrapperElement = computed(() => {
  return props.accessibilityRole === OzTabsAccessibilityRole.Tabs ? 'div' : 'nav'
})

const wrapperAttributes = computed(() => {
  return props.accessibilityRole === OzTabsAccessibilityRole.Tabs ? {} : { 'aria-label': props.ariaLabel }
})

const listAttributes = computed(() => {
  return isUsingTabsAccessibilityRole.value ? { 'aria-label': props.ariaLabel } : {}
})

const listRole = computed(() => {
  return isUsingTabsAccessibilityRole.value ? 'tablist' : 'list'
})

const listItemRole = computed(() => {
  return isUsingTabsAccessibilityRole.value ? 'presentation' : 'listitem'
})

const isUsingTabsAccessibilityRole = computed(() => {
  return props.accessibilityRole === OzTabsAccessibilityRole.Tabs
})

function computeTabAttributes(tab: OzTabsButtonProps): Record<string, any> {
  return isUsingTabsAccessibilityRole.value
    ? {
        id: tab.key,
        role: 'tab',
        'aria-controls': tab.ariaControls,
        'aria-label': tab.iconName ? tab.text : undefined,
        'aria-selected': currentTabId.value === tab.key ? 'true' : 'false',
        tabindex: currentTabId.value === tab.key ? 0 : -1,
      }
    : {
        role: tab.href ? 'link' : 'button',
      }
}

const repositionIndicator = async () => {
  await nextTick()
  if (!highlightedElement.value) return

  const indicatorElement = indicator.value
  if (indicatorElement === null) return

  // If indicator height is provided, override the height from the size preset
  if (props.indicatorHeight) {
    indicatorElement.style.height = props.indicatorHeight + 'px'
  }

  if (props.indicatorWidth) {
    indicatorElement.style.width = props.indicatorWidth + 'px'
    indicatorElement.style.left =
      highlightedElement.value.offsetLeft + (highlightedElement.value.clientWidth - props.indicatorWidth) / 2 + 'px'
  } else {
    indicatorElement.style.width = highlightedElement.value.offsetWidth + 'px'
    indicatorElement.style.left = highlightedElement.value.offsetLeft + 'px'
  }
}

/**
 * Focus the first tab.
 */
function focusFirstTab(): void {
  if (tabsRef.value[0] === null) return
  const el = tabsRef.value[0].el?.firstChild as HTMLElement
  el.focus()
}

/**
 * Focus the last tab.
 */
function focusLastTab(): void {
  if (tabsRef.value.length === 0) return
  const lastTabEl = tabsRef.value[tabsRef.value.length - 1]
  if (lastTabEl !== null) {
    const el = lastTabEl.el.firstChild as HTMLElement
    el.focus()
  }
}

/**
 * When pressing "Home", focus the first tab.
 */
function handleHomeKey(event: KeyboardEvent): void {
  event.preventDefault()
  focusFirstTab()
}

/**
 * When pressing "End", focus the last tab.
 */
function handleEndKey(event: KeyboardEvent): void {
  event.preventDefault()
  focusLastTab()
}

/**
 * When pressing the left arrow, focus the previous tab.
 * If on first tab, focus the last tab.
 */
function handleArrowLeft(event: KeyboardEvent): void {
  const target = event.target as HTMLElement
  const wrapper = target.parentElement
  if (wrapper?.previousElementSibling) {
    const previousSibling = wrapper.previousElementSibling.firstChild as HTMLElement
    previousSibling?.focus()
  } else {
    focusLastTab()
  }
}

/**
 * When pressing the right arrow, focus the next tab.
 * If on last tab, focus the first tab.
 */
function handleArrowRight(event: KeyboardEvent): void {
  const target = event.target as HTMLElement
  const wrapper = target.parentElement
  if (wrapper?.nextElementSibling) {
    const nextSibling = wrapper.nextElementSibling.firstChild as HTMLElement
    nextSibling?.focus()
  } else {
    focusFirstTab()
  }
}

watch(
  [
    () => currentTabId.value,
    () => props.placementMode,
    () => props.sizePreset,
    () => props.tabsContainerClasses,
    () => props.indicatorWidth,
  ],
  repositionIndicator,
  { immediate: true },
)

let removeKeyboardShortcutsListener = () => {}

onMounted(() => {
  if (rootEl.value) {
    resizeObserver.value = observeSizeChange(rootEl.value, repositionIndicator)
  }
  if (list.value) {
    removeKeyboardShortcutsListener = tinykeys(list.value, {
      Home: handleHomeKey,
      End: handleEndKey,
      ArrowLeft: handleArrowLeft,
      ArrowRight: handleArrowRight,
    })
  }
})

onBeforeUnmount(() => {
  if (rootEl.value) {
    resizeObserver.value?.disconnect()
    resizeObserver.value = null
  }
  removeKeyboardShortcutsListener()
})

return { __sfc: true,OzTabsAccessibilityRole, OzTabsPlacementMode, OzTabsSizePreset, OzIcon, props, currentTabId, emit, resizeObserver, rootEl, indicator, list, tabsRef, setTabRef, highlightedElement, wrapperElement, wrapperAttributes, listAttributes, listRole, listItemRole, isUsingTabsAccessibilityRole, computeTabAttributes, repositionIndicator, focusFirstTab, focusLastTab, handleHomeKey, handleEndKey, handleArrowLeft, handleArrowRight, removeKeyboardShortcutsListener, OzPlainButton, OzPlainButtonColorScheme }
}

})